import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle,
  Edit3,
  Filter,
  Users,
  User,
  BookOpen,
  GraduationCap,
  Building,
  RefreshCw,
  Trash2,
  AlertTriangle,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'; // REMOVED - Using native HTML select instead
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Default time slots (1.5 hour slots)
const DEFAULT_TIME_SLOTS = [
  '09:00-10:30',
  '10:30-12:00', 
  '12:00-13:30',
  '13:30-15:00',
  '15:00-16:30',
  '16:30-18:00'
];

const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS); // Will be updated with custom slots
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  
  // Status update modal
  const [statusEditingSession, setStatusEditingSession] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionUpdate, setSessionUpdate] = useState({
    status: undefined,
    notes: '',
    scheduled_date: ''
  });

  // Full edit session modal
  const [editingSession, setEditingSession] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({
    scheduled_date: '',
    time_slot: undefined,
    teacher_id: undefined,
    subject_id: undefined,
    classroom_id: undefined,
    notes: ''
  });
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  // Delete session functionality
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Multi-select functionality
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  
  // Deleted sessions view
  const [showDeletedSessions, setShowDeletedSessions] = useState(false);
  const [deletedSessions, setDeletedSessions] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Fetch static data only once on component mount
  useEffect(() => {
    fetchBatches();
    fetchTeachers();
    fetchSubjects();
    fetchClassrooms();
    fetchTimeSlots();
  }, []); // Empty dependency array - run once on mount

  // Fetch sessions when filters change
  useEffect(() => {
    console.log('[USEEFFECT] Filter changed, fetching sessions...', { statusFilter, batchFilter, dateFromFilter, dateToFilter });
    
    // Clear sessions immediately when filter changes to prevent showing stale data
    setLoading(true);
    setSessions([]);
    
    fetchSessions();
  }, [statusFilter, batchFilter, dateFromFilter, dateToFilter]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (batchFilter !== 'all') params.append('batch_id', batchFilter);
      if (dateFromFilter) params.append('date_from', dateFromFilter);
      if (dateToFilter) params.append('date_to', dateToFilter);
      
      // Add cache-busting parameter to prevent stale data
      params.append('_t', Date.now().toString());
      
      const url = `${API}/sessions?${params}`;
      console.log('[SESSION FETCH] URL:', url);
      console.log('[SESSION FETCH] Filters:', { statusFilter, batchFilter, dateFromFilter, dateToFilter });
      
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Verify response is an array
      const sessionData = Array.isArray(response.data) ? response.data : [];
      
      console.log('[SESSION FETCH] Response count:', sessionData.length);
      console.log('[SESSION FETCH] Response type:', typeof response.data, Array.isArray(response.data) ? 'array' : 'not array');
      
      if (sessionData.length > 0) {
        console.log('[SESSION FETCH] First 3 sessions:', sessionData.slice(0, 3).map(s => ({
          id: s.id,
          batch: s.batch_name,
          date: s.scheduled_date,
          time: s.time_slot
        })));
        
        // Check for the specific time slots the user is looking for
        const targetSlots = ['10:30-12:00', '13:30-15:00'];
        const targetDate = '2025-11-17';
        const matchingSessions = sessionData.filter(s => 
          targetSlots.includes(s.time_slot) && s.scheduled_date >= targetDate
        );
        console.log(`[SESSION FETCH] Found ${matchingSessions.length} sessions in slots ${targetSlots.join(', ')} from ${targetDate} onwards`);
      }
      
      console.log('[SESSION FETCH] Setting sessions state with', sessionData.length, 'sessions');
      setSessions(sessionData);
      
      // Log after state update (will show in next render)
      setTimeout(() => {
        console.log('[SESSION FETCH] State updated. Current sessions.length:', sessionData.length);
      }, 100);
    } catch (error) {
      console.error('[SESSION FETCH] Error fetching sessions:', error);
      console.error('[SESSION FETCH] Error response:', error.response?.data);
      console.error('[SESSION FETCH] Error status:', error.response?.status);
      toast.error('Failed to load sessions');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('[BATCH FETCH] Fetching batches from:', `${API}/batches`);
      // Exclude archived batches from Session Management
      const response = await axios.get(`${API}/batches`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_archived: false }
      });
      const batchData = response.data || [];
      console.log('[BATCH FETCH] Received', batchData.length, 'active batches (archived excluded)');
      console.log('[BATCH FETCH] Sample batch:', batchData[0]);
      setBatches(batchData);
    } catch (error) {
      console.error('[BATCH FETCH] Error fetching batches:', error);
      console.error('[BATCH FETCH] Error details:', error.response?.data);
      setBatches([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeachers(response.data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classrooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassrooms(response.data || []);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      setClassrooms([]);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get custom time slots from API
      const customSlots = response.data || [];
      
      // Format custom slots to match the time format (HH:MM-HH:MM)
      const customSlotStrings = customSlots.map(slot => 
        `${slot.start_time}-${slot.end_time}`
      );
      
      // Combine default and custom slots, removing duplicates
      const allSlots = [...new Set([...DEFAULT_TIME_SLOTS, ...customSlotStrings])];
      
      // Sort time slots chronologically
      allSlots.sort((a, b) => {
        const [aStart] = a.split('-');
        const [bStart] = b.split('-');
        return aStart.localeCompare(bStart);
      });
      
      setTimeSlots(allSlots);
      console.log('[TIME SLOTS] Loaded:', allSlots.length, 'slots (', customSlots.length, 'custom +', DEFAULT_TIME_SLOTS.length, 'default)');
    } catch (error) {
      console.error('Error fetching time slots:', error);
      // Fall back to default slots if API fails
      setTimeSlots(DEFAULT_TIME_SLOTS);
    }
  };

  const handleStatusUpdate = (session) => {
    setStatusEditingSession(session);
    setSessionUpdate({
      status: session.status || '',
      notes: session.notes || '',
      scheduled_date: session.scheduled_date || ''
    });
    setIsModalOpen(true);
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    if (!statusEditingSession) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sessions/${statusEditingSession.id}`, sessionUpdate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session updated successfully');
      setIsModalOpen(false);
      setStatusEditingSession(null);
      fetchSessions();
    } catch (error) {
      console.error('Error updating session:', error);
      toast.error(error.response?.data?.detail || 'Failed to update session');
    }
  };

  const handleCancelSession = async (session) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to cancel this session?\n\n` +
      `${session.subject_name} - Session #${session.session_number}\n` +
      `Date: ${new Date(session.scheduled_date).toLocaleDateString()}\n` +
      `Time: ${session.time_slot}\n\n` +
      `This action cannot be undone.`
    );
    
    if (!isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sessions/${session.id}`, {
        status: 'cancelled',
        notes: `Session cancelled on ${new Date().toLocaleDateString()}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session cancelled successfully');
      fetchSessions();
    } catch (error) {
      console.error('Error cancelling session:', error);
      // Check if it's a permission error (403)
      if (error.response?.status === 403) {
        toast.error(`❌ Permission Denied: ${error.response?.data?.detail || 'You don\'t have permission to edit sessions'}. Contact Super Admin to grant access.`, {
          duration: 6000
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to cancel session');
      }
    }
  };

  const handleEditSession = (session) => {
    console.log('Opening edit modal for session:', session);
    console.log('Teachers data:', teachers);
    console.log('Subjects data:', subjects);
    console.log('Classrooms data:', classrooms);
    
    setEditingSession(session);
    setEditData({
      scheduled_date: session.scheduled_date || '',
      time_slot: session.time_slot || undefined,
      teacher_id: session.teacher_id || undefined,
      subject_id: session.subject_id || undefined,
      classroom_id: session.classroom_id || undefined,
      notes: session.notes || ''
    });
    
    console.log('Setting modal open to true');
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingSession) return;

    try {
      const token = localStorage.getItem('token');
      
      console.log('[EDIT SESSION] Current editData:', editData);
      console.log('[EDIT SESSION] Original session time_slot:', editingSession.time_slot);
      
      // Build update data - only send fields that are provided and have changed
      const updateData = {};
      
      if (editData.scheduled_date && editData.scheduled_date !== editingSession.scheduled_date) {
        updateData.scheduled_date = editData.scheduled_date;
        console.log('[EDIT SESSION] Adding scheduled_date to update:', editData.scheduled_date);
      }
      
      // CRITICAL: Always send time_slot if it's been set in the form, even if empty
      if (editData.time_slot !== undefined && editData.time_slot !== editingSession.time_slot) {
        updateData.time_slot = editData.time_slot;
        console.log('[EDIT SESSION] Adding time_slot to update:', editData.time_slot);
      }
      
      if (editData.classroom_id && editData.classroom_id !== editingSession.classroom_id) {
        updateData.classroom_id = editData.classroom_id;
        console.log('[EDIT SESSION] Adding classroom_id to update:', editData.classroom_id);
      }
      
      if (editData.teacher_id && editData.teacher_id !== editingSession.teacher_id) {
        updateData.teacher_id = editData.teacher_id;
        console.log('[EDIT SESSION] Adding teacher_id to update:', editData.teacher_id);
      }
      
      if (editData.subject_id && editData.subject_id !== editingSession.subject_id) {
        updateData.subject_id = editData.subject_id;
        console.log('[EDIT SESSION] Adding subject_id to update:', editData.subject_id);
      }
      
      if (editData.notes !== undefined && editData.notes !== editingSession.notes) {
        updateData.notes = editData.notes;
        console.log('[EDIT SESSION] Adding notes to update');
      }
      
      console.log('[EDIT SESSION] Final updateData being sent:', updateData);
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes detected');
        return;
      }
      
      const response = await axios.put(`${API}/sessions/${editingSession.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[EDIT SESSION] Update response:', response.data);
      
      toast.success('Session updated successfully');
      setIsEditModalOpen(false);
      setEditingSession(null);
      fetchSessions();
    } catch (error) {
      console.error('[EDIT SESSION] Error updating session:', error);
      console.error('[EDIT SESSION] Error response:', error.response?.data);
      
      // Check if it's a permission error (403)
      if (error.response?.status === 403) {
        toast.error(`❌ Permission Denied: ${error.response?.data?.detail || 'You don\'t have permission to edit sessions'}. Contact Super Admin to grant access.`, {
          duration: 6000
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update session');
      }
    }
  };

  // Delete functions
  const handleDeleteSession = async (sessionId) => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('[DELETE SESSION] Attempting to delete session:', sessionId);
      console.log('[DELETE SESSION] Token exists:', !!token);
      console.log('[DELETE SESSION] Token preview:', token ? token.substring(0, 20) + '...' : 'MISSING');
      
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      
      await axios.delete(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Session deleted successfully');
      setIsDeleteModalOpen(false);
      setSessionToDelete(null);
      fetchSessions(); // Refresh the list
    } catch (error) {
      console.error('[DELETE SESSION] Error deleting session:', error);
      console.error('[DELETE SESSION] Error response:', error.response);
      
      // Check for different error types
      if (error.response?.status === 401) {
        toast.error('❌ Session expired or invalid. Please login again.', {
          duration: 6000
        });
      } else if (error.response?.status === 403) {
        toast.error(`❌ Permission Denied: ${error.response?.data?.detail || 'You don\'t have permission to delete sessions'}. Contact Super Admin to grant access.`, {
          duration: 6000
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete session');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearAllSessions = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.deleted_count} sessions deleted successfully`);
      setIsClearAllModalOpen(false);
      fetchSessions(); // Refresh the list
    } catch (error) {
      console.error('Error clearing all sessions:', error);
      toast.error('Failed to clear all sessions');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (session) => {
    setSessionToDelete(session);
    setIsDeleteModalOpen(true);
  };

  // Multi-select handlers
  const toggleSelectSession = (sessionId) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.length === 0) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/sessions/bulk-delete`, 
        selectedSessions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.deleted_count} sessions deleted successfully (recoverable for 30 days)`);
      setIsBulkDeleteModalOpen(false);
      setSelectedSessions([]);
      fetchSessions();
    } catch (error) {
      console.error('Error bulk deleting sessions:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete sessions');
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch deleted sessions
  const fetchDeletedSessions = async () => {
    setLoadingDeleted(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedSessions(response.data);
      toast.success(`Found ${response.data.length} deleted sessions`);
    } catch (error) {
      console.error('Error fetching deleted sessions:', error);
      toast.error('Failed to load deleted sessions');
      setDeletedSessions([]);
    } finally {
      setLoadingDeleted(false);
    }
  };

  // Restore a deleted session
  const handleRestoreSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sessions/${sessionId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Session restored successfully');
      fetchDeletedSessions(); // Refresh deleted list
      fetchSessions(); // Refresh active sessions
    } catch (error) {
      console.error('Error restoring session:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore session');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Group subjects by stream and semester for reschedule modal
  const getGroupedSubjects = () => {
    const grouped = {};

    // Safety check - ensure subjects is an array
    if (!Array.isArray(subjects)) {
      return grouped;
    }

    subjects.forEach(subject => {
      const stream = subject.stream || 'Other';
      const year = subject.year || 'Other';
      const semester = subject.semester || 'Other';
      
      if (!grouped[stream]) {
        grouped[stream] = {};
      }
      if (!grouped[stream][year]) {
        grouped[stream][year] = {};
      }
      if (!grouped[stream][year][semester]) {
        grouped[stream][year][semester] = [];
      }
      
      grouped[stream][year][semester].push(subject);
    });

    // Sort subjects within each group
    Object.keys(grouped).forEach(stream => {
      Object.keys(grouped[stream]).forEach(year => {
        Object.keys(grouped[stream][year]).forEach(semester => {
          grouped[stream][year][semester].sort((a, b) => a.name.localeCompare(b.name));
        });
      });
    });

    return grouped;
  };

  const renderSubjectGroups = () => {
    const groupedSubjects = getGroupedSubjects();
    const elements = [];

    // Safety check - if no subjects, return a message with valid value
    if (Object.keys(groupedSubjects).length === 0) {
      return (
        <SelectItem value="no-subjects-available" disabled>
          No subjects available
        </SelectItem>
      );
    }

    Object.keys(groupedSubjects).sort().forEach(stream => {
      // Stream header as disabled SelectItem
      elements.push(
        <SelectItem key={`stream-${stream}`} value={`stream-header-${stream}`} disabled className="px-2 py-2 text-xs font-bold text-gray-500 uppercase tracking-wide border-b">
          {stream}
        </SelectItem>
      );

      // Skill Enhancement: flat list, no year/semester subheaders
      if (stream === 'Skill Enhancement') {
        const allSubjects = [];
        Object.keys(groupedSubjects[stream]).forEach(y => {
          Object.keys(groupedSubjects[stream][y]).forEach(s => {
            allSubjects.push(...groupedSubjects[stream][y][s]);
          });
        });
        allSubjects.sort((a, b) => a.name.localeCompare(b.name)).filter(subject => subject.id && subject.id.trim() !== '').forEach(subject => {
          elements.push(
            <SelectItem key={subject.id} value={subject.id} className="pl-6">
              <div className="flex items-center justify-between w-full">
                <span>{subject.name}</span>
                {subject.id === editingSession?.subject_id && (
                  <span className="text-xs text-blue-600 ml-2">(Current)</span>
                )}
              </div>
            </SelectItem>
          );
        });
        return;
      }

      Object.keys(groupedSubjects[stream]).sort().forEach(year => {
        Object.keys(groupedSubjects[stream][year]).sort().forEach(semester => {
          // Year-Semester subheader as disabled SelectItem
          const yearSemesterLabel = `${year === 'FY' ? 'First Year' : 
                                      year === 'SY' ? 'Second Year' : 
                                      year === 'TY' ? 'Third Year' : 
                                      year === '4th Year' ? 'Fourth Year' : year} - ${semester}`;
          
          elements.push(
            <SelectItem key={`header-${stream}-${year}-${semester}`} value={`header-${stream}-${year}-${semester}`} disabled className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50">
              {yearSemesterLabel}
            </SelectItem>
          );

          // Subjects in this group
          groupedSubjects[stream][year][semester].filter(subject => subject.id && subject.id.trim() !== '').forEach(subject => {
            elements.push(
              <SelectItem key={subject.id} value={subject.id} className="pl-6">
                <div className="flex items-center justify-between w-full">
                  <span>{subject.name}</span>
                  {subject.id === editingSession?.subject_id && (
                    <span className="text-xs text-blue-600 ml-2">(Current)</span>
                  )}
                </div>
              </SelectItem>
            );
          });
        });
      });
    });

    return elements;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const groupSessionsByDate = () => {
    console.log('[GROUP SESSIONS] Starting to group', sessions.length, 'sessions');
    console.log('[GROUP SESSIONS] Current filters:', { statusFilter, batchFilter, dateFromFilter, dateToFilter });
    
    // Check if sessions have correct batch_id
    if (batchFilter !== 'all' && sessions.length > 0) {
      const batchIds = [...new Set(sessions.map(s => s.batch_id))];
      console.log('[GROUP SESSIONS] Unique batch_ids in sessions:', batchIds);
      console.log('[GROUP SESSIONS] Expected batch_id:', batchFilter);
      const wrongBatchSessions = sessions.filter(s => s.batch_id !== batchFilter);
      if (wrongBatchSessions.length > 0) {
        console.warn('[GROUP SESSIONS] WARNING:', wrongBatchSessions.length, 'sessions have wrong batch_id!');
        console.warn('[GROUP SESSIONS] Sample wrong sessions:', wrongBatchSessions.slice(0, 3).map(s => ({
          id: s.id,
          batch_id: s.batch_id,
          batch_name: s.batch_name
        })));
      }
    }
    
    const grouped = {};
    sessions.forEach(session => {
      const date = session.scheduled_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(session);
    });
    
    // Sort dates
    const sortedDates = Object.keys(grouped).sort();
    const result = {};
    sortedDates.forEach(date => {
      result[date] = grouped[date].sort((a, b) => a.time_slot.localeCompare(b.time_slot));
    });
    
    console.log('[GROUP SESSIONS] Grouped into', Object.keys(result).length, 'dates');
    console.log('[GROUP SESSIONS] Sessions per date:', Object.entries(result).map(([date, sessions]) => `${date}: ${sessions.length}`));
    
    return result;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedSessions = groupSessionsByDate();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Session Management</h1>
          <p className="text-gray-600">Track and manage individual class sessions</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{sessions.length} sessions</span>
            {selectedSessions.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selectedSessions.length} selected
              </Badge>
            )}
          </div>
          {selectedSessions.length > 0 && (
            <Button
              onClick={() => setIsBulkDeleteModalOpen(true)}
              variant="outline"
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedSessions.length})
            </Button>
          )}
          <Button
            onClick={() => {
              setShowDeletedSessions(true);
              fetchDeletedSessions();
            }}
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            View Deleted Sessions
          </Button>
          <Button
            onClick={() => setIsClearAllModalOpen(true)}
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            disabled={sessions.length === 0}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Sessions
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-enhanced">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Batch</label>
              <select
                value={batchFilter}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  console.log('[BATCH FILTER] Selected batch ID:', selectedValue);
                  const selectedBatch = batches.find(b => b.id === selectedValue);
                  console.log('[BATCH FILTER] Selected batch details:', selectedBatch);
                  setBatchFilter(selectedValue);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="batch-filter"
              >
                <option value="all">All Batches</option>
                {batches.filter(batch => batch.id && batch.id.trim() !== '').map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="form-input"
                data-testid="date-from-filter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="form-input"
                data-testid="date-to-filter"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Display */}
      {Object.keys(groupedSessions).length === 0 ? (
        <Card className="card-enhanced">
          <CardContent className="p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Sessions Found
            </h3>
            <p className="text-gray-600 mb-6">
              No sessions match your current filters. Try adjusting the filters above.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedSessions).map(([date, dateSessions]) => (
            <Card key={date} className="card-enhanced">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                    {formatDate(date)}
                    <Badge variant="outline" className="ml-4">
                      {dateSessions.length} session{dateSessions.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  {sessions.length > 0 && (
                    <div className="flex items-center space-x-2 text-sm font-normal">
                      <input
                        type="checkbox"
                        checked={selectedSessions.length === sessions.length && sessions.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-600">Select All</span>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {dateSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid={`session-${session.id}`}
                  >
                    {/* Checkbox for multi-select */}
                    <div className="flex items-center mr-4">
                      <input
                        type="checkbox"
                        checked={selectedSessions.includes(session.id)}
                        onChange={() => toggleSelectSession(session.id)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{session.time_slot}</span>
                        </div>
                        <div className="text-xs text-gray-500">Session #{session.session_number}</div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <BookOpen className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">{session.subject_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{session.batch_name}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <GraduationCap className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-900">{session.teacher_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Building className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{session.classroom_name}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Badge className={`flex items-center space-x-1 ${getStatusColor(session.status)}`}>
                          {getStatusIcon(session.status)}
                          <span className="capitalize">{session.status}</span>
                        </Badge>
                        
                        <div className="flex space-x-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditSession(session)}
                            className="p-2 h-8 w-8"
                            data-testid={`edit-session-${session.id}`}
                            title="Edit session"
                          >
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          
                          {session.status === 'scheduled' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelSession(session)}
                                className="p-2 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`cancel-session-${session.id}`}
                                title="Cancel session"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          
                          {/* Delete button - always visible for admins */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDeleteModal(session)}
                            className="p-2 h-8 w-8 text-red-800 hover:text-red-900 hover:bg-red-100 border-red-300"
                            data-testid={`delete-session-${session.id}`}
                            title="Delete session permanently"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Session Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="session-modal-title">
              Edit Session
            </DialogTitle>
            <DialogDescription>
              Update session status, reschedule date, or add notes.
            </DialogDescription>
          </DialogHeader>
          
          {editingSession && (
            <form onSubmit={handleUpdateSession} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <div className="text-sm font-medium text-blue-800 mb-1">
                  {editingSession.subject_name} - Session #{editingSession.session_number}
                </div>
                <div className="text-xs text-blue-600">
                  {editingSession.batch_name} • {editingSession.teacher_name}
                </div>
              </div>

              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={sessionUpdate.status || ''}
                  onChange={(e) => setSessionUpdate({...sessionUpdate, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="session-status-select"
                >
                  <option value="">Select status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reschedule Date (Optional)
                </label>
                <Input
                  type="date"
                  value={sessionUpdate.scheduled_date}
                  onChange={(e) => setSessionUpdate({...sessionUpdate, scheduled_date: e.target.value})}
                  className="form-input"
                  data-testid="session-reschedule-date"
                />
              </div>

              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={sessionUpdate.notes}
                  onChange={(e) => setSessionUpdate({...sessionUpdate, notes: e.target.value})}
                  className="form-input resize-none"
                  rows="3"
                  placeholder="Add notes about this session (optional)"
                  data-testid="session-notes"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1"
                  data-testid="cancel-session-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-primary"
                  data-testid="save-session-btn"
                >
                  Update Session
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Session Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="edit-session-description">
          <DialogHeader>
            <DialogTitle data-testid="reschedule-modal-title">
              Edit Session
            </DialogTitle>
            <p id="edit-session-description" className="sr-only">
              Edit session details including date, time slot, teacher, subject, classroom, and notes
            </p>
          </DialogHeader>
          
          {editingSession && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="bg-orange-50 p-3 rounded-lg mb-4">
                <div className="text-sm font-medium text-orange-800 mb-1">
                  {editingSession.subject_name} - Session #{editingSession.session_number}
                </div>
                <div className="text-xs text-orange-600">
                  Current: {new Date(editingSession.scheduled_date).toLocaleDateString()} • {editingSession.time_slot} • {editingSession.teacher_name}
                </div>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date
                </label>
                <Input
                  type="date"
                  value={editData.scheduled_date}
                  onChange={(e) => setEditData({...editData, scheduled_date: e.target.value})}
                  className="form-input"
                  required
                  data-testid="reschedule-date-input"
                />
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Slot
                </label>
                <select
                  value={editData.time_slot || ''}
                  onChange={(e) => setEditData({...editData, time_slot: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reschedule-time-select"
                >
                  <option value="">Select time slot</option>
                  {timeSlots.map(slot => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <select
                  value={editData.subject_id || ''}
                  onChange={(e) => setEditData({...editData, subject_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reschedule-subject-select"
                >
                  <option value="">Select subject</option>
                  {Array.isArray(subjects) && subjects.length > 0 ? (
                    subjects.filter(subject => subject.id && subject.id.trim() !== '').map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.stream || 'Other'} - {subject.year || 'N/A'} - {subject.name}
                        {subject.id === editingSession?.subject_id ? ' (Current)' : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>No subjects available</option>
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Faculty
                </label>
                <select
                  value={editData.teacher_id || ''}
                  onChange={(e) => setEditData({...editData, teacher_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reschedule-teacher-select"
                >
                  <option value="">Select teacher (optional)</option>
                  {Array.isArray(teachers) && teachers.length > 0 ? (
                    teachers.filter(teacher => teacher.id && teacher.id.trim() !== '').map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                        {teacher.id === editingSession?.teacher_id ? ' (Current)' : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>No teachers available</option>
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Classroom
                </label>
                <select
                  value={editData.classroom_id || ''}
                  onChange={(e) => setEditData({...editData, classroom_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reschedule-classroom-select"
                >
                  <option value="">Select classroom (optional)</option>
                  {Array.isArray(classrooms) && classrooms.length > 0 ? (
                    classrooms.filter(classroom => classroom.id && classroom.id.trim() !== '').map(classroom => (
                      <option key={classroom.id} value={classroom.id}>
                        {classroom.name}
                        {classroom.id === editingSession?.classroom_id ? ' (Current)' : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>No classrooms available</option>
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  className="form-input resize-none"
                  rows="3"
                  placeholder="Add any notes about this session change..."
                  data-testid="reschedule-notes-input"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1"
                  data-testid="cancel-reschedule-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="save-reschedule-btn"
                >
                  Reschedule
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Session Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              <span>Delete Session</span>
            </DialogTitle>
            <DialogDescription>
              Confirm soft deletion of this session. It can be recovered within 30 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800">
                    Soft Delete (Recoverable)
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>This session will be deleted but can be recovered within 30 days from the "View Deleted Sessions" option.</p>
                  </div>
                </div>
              </div>
            </div>

            {sessionToDelete && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this session?
                </p>
                <div className="bg-gray-50 border rounded-lg p-3">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <BookOpen className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">{sessionToDelete.subject_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {new Date(sessionToDelete.scheduled_date).toLocaleDateString()} at {sessionToDelete.time_slot}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <GraduationCap className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{sessionToDelete.teacher_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{sessionToDelete.batch_name}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDeleteSession(sessionToDelete.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Session
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Sessions Confirmation Modal */}
      <Dialog open={isClearAllModalOpen} onOpenChange={setIsClearAllModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Clear All Sessions</span>
            </DialogTitle>
            <DialogDescription>
              Permanently delete all sessions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <X className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">
                    ⚠️ CRITICAL ACTION - Read Carefully
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p className="font-bold">This will delete ALL {sessions.length} sessions in the system!</p>
                    <p className="mt-2">Sessions will be recoverable for 30 days, but this action should only be used for complete system reset.</p>
                    <p className="mt-2 font-semibold">Consider using "Delete Selected" instead for specific sessions.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-600 font-medium">
                This will delete:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• All scheduled sessions</li>
                <li>• All completed sessions</li>
                <li>• All cancelled sessions</li>
                <li>• All session data and records</li>
              </ul>
              <p className="text-sm text-red-600 font-medium mt-3">
                Type "DELETE ALL" to confirm this action:
              </p>
              <Input
                placeholder="Type DELETE ALL to confirm"
                value={confirmText || ''}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsClearAllModalOpen(false);
                setConfirmText('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleClearAllSessions}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeleting || confirmText !== 'DELETE ALL'}
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Sessions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Selected Sessions Modal */}
      <Dialog open={isBulkDeleteModalOpen} onOpenChange={setIsBulkDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Delete Selected Sessions</span>
            </DialogTitle>
            <DialogDescription>
              Soft delete {selectedSessions.length} selected session(s). Recoverable within 30 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <Trash2 className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-orange-800">
                    Delete {selectedSessions.length} Selected Session{selectedSessions.length !== 1 ? 's' : ''}
                  </h3>
                  <div className="mt-2 text-sm text-orange-700">
                    <p>These sessions will be deleted but can be recovered within 30 days if needed.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                You have selected <strong>{selectedSessions.length}</strong> session{selectedSessions.length !== 1 ? 's' : ''} to delete.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <p className="font-medium">💡 Recovery Option:</p>
                <p>Deleted sessions can be restored from the "Deleted Sessions" view within 30 days.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedSessions.length} Session{selectedSessions.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deleted Sessions Modal */}
      <Dialog open={showDeletedSessions} onOpenChange={setShowDeletedSessions}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Trash2 className="w-5 h-5 text-blue-600" />
              <span>Deleted Sessions (Recoverable)</span>
            </DialogTitle>
            <DialogDescription>
              View and restore soft-deleted sessions. Sessions are permanently deleted after 30 days.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingDeleted ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : deletedSessions.length === 0 ? (
              <div className="text-center py-8">
                <Trash2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No deleted sessions found</p>
                <p className="text-sm text-gray-400 mt-1">Deleted sessions can be recovered within 30 days</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>{deletedSessions.length}</strong> deleted session{deletedSessions.length !== 1 ? 's' : ''} found. 
                    These can be restored within 30 days of deletion.
                  </p>
                </div>

                <div className="space-y-3">
                  {deletedSessions.map((session) => (
                    <div
                      key={session.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="font-medium text-gray-900">
                              {session.subject_name || 'Unknown Subject'}
                            </span>
                            {session.batch_name && (
                              <Badge variant="outline">{session.batch_name}</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {session.scheduled_date}
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {session.time_slot}
                            </div>
                            {session.teacher_name && (
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-1" />
                                {session.teacher_name}
                              </div>
                            )}
                            <div className="text-red-600 text-xs">
                              Deleted: {new Date(session.deleted_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleRestoreSession(session.id)}
                          variant="outline"
                          size="sm"
                          className="ml-4 text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeletedSessions(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionManagement;